import { FC } from 'react';
import { ICoachSessionMetrics } from '../../../endpoints/coachSessions';
import styles from './AnalysisPanel.module.scss';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PROFILE_COLORS: Record<string, { bg: string; color: string }> = {
  PHYSICAL_DOMINANT: { bg: 'rgba(245, 179, 80, 0.15)', color: '#c47d00' },
  COGNITIVE_DOMINANT: { bg: 'rgba(6, 52, 68, 0.12)', color: '#063444' },
  BALANCED: { bg: 'rgba(77, 210, 146, 0.15)', color: '#1a8a5a' },
};

const PROFILE_LABELS: Record<string, string> = {
  PHYSICAL_DOMINANT: 'Fisico dominante',
  COGNITIVE_DOMINANT: 'Cognitivo dominante',
  BALANCED: 'Bilanciato',
};

function acwrStatus(val?: number): 'ok' | 'warn' {
  if (val == null) return 'ok';
  return val > 1.5 || val < 0.8 ? 'warn' : 'ok';
}

function buildSuggestions(m: ICoachSessionMetrics): string[] {
  const suggestions: string[] = [];
  if (m.avgIPS != null && m.avgIPS > 7.5) {
    suggestions.push('Carico superiore alla soglia MD-3 standard (IPS > 7.5)');
  }
  if (m.cognitive != null && m.cognitive > 4) {
    suggestions.push(
      'Blocco cognitivo elevato (>4) → considera inserire una rotazione',
    );
  }
  if (m.acwr != null && m.acwr > 1.3) {
    suggestions.push(
      `ACWR proiettato alto (${m.acwr.toFixed(2)}) → rischio overload acuto`,
    );
  }
  if (
    m.loadProfile === 'PHYSICAL_DOMINANT' &&
    m.ewtPct != null &&
    m.ewtPct < 60
  ) {
    suggestions.push('Sessione fisica con EWT% basso: valuta riduzione tempi morti');
  }
  return suggestions;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AnalysisPanelProps {
  metrics?: ICoachSessionMetrics;
}

export const AnalysisPanel: FC<AnalysisPanelProps> = ({ metrics: m }) => {
  if (!m) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelScroll}>
          <p className={styles.emptyMetrics}>
            Aggiungi blocchi per vedere l'analisi
          </p>
        </div>
      </div>
    );
  }

  const profile = m.loadProfile;
  const profileStyle = profile ? PROFILE_COLORS[profile] : undefined;
  const profileLabel = profile ? PROFILE_LABELS[profile] : '—';

  const ipsRatio =
    m.avgIPS != null && m.avgCLS != null && m.avgCLS > 0
      ? m.avgIPS / m.avgCLS
      : null;

  const suggestions = buildSuggestions(m);

  return (
    <div className={styles.panel}>
      <div className={styles.panelScroll}>
        {/* ─── 4 strutture Sievers ─────────────────────── */}
        <div className={styles.sectionCard}>
          <div className={styles.sectionTitle}>
            4 strutture di carico (Sievers)
          </div>

          {[
            { label: 'Condizionale', value: m.conditional },
            { label: 'Coordinativa', value: m.coordinative },
            { label: 'Cognitiva', value: m.cognitive },
            { label: 'Socio-affett.', value: m.socioAffective },
          ].map(({ label, value }) => (
            <div key={label} className={styles.structureRow}>
              <span className={styles.structureName}>{label}</span>
              <div className={styles.structureBarTrack}>
                <div
                  className={styles.structureBar}
                  style={{ width: `${((value ?? 0) / 10) * 100}%` }}
                />
              </div>
              <span className={styles.structureValue}>
                {value?.toFixed(1) ?? '—'}
              </span>
            </div>
          ))}

          {profile && (
            <div className={styles.profileRow}>
              <span className={styles.profileLabel}>Profilo:</span>
              <span
                className={styles.profileChip}
                style={{
                  background: profileStyle?.bg,
                  color: profileStyle?.color,
                }}
              >
                {profileLabel}
              </span>
            </div>
          )}
          {ipsRatio != null && (
            <div className={styles.profileRow}>
              <span className={styles.profileLabel}>Ratio IPS/CLS:</span>
              <span className={styles.kvValue}>{ipsRatio.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* ─── Carico settimanale ──────────────────────── */}
        {(m.acwr != null || m.monotonia != null || m.strain != null) && (
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>Carico settimanale</div>
            {m.acwr != null && (
              <div className={styles.kvRow}>
                <span className={styles.kvLabel}>ACWR attuale</span>
                <span
                  className={`${styles.kvValue} ${
                    acwrStatus(m.acwr) === 'warn' ? styles.kvWarn : styles.kvOk
                  }`}
                >
                  {m.acwr.toFixed(2)}
                </span>
              </div>
            )}
            {m.monotonia != null && (
              <div className={styles.kvRow}>
                <span className={styles.kvLabel}>Monotonia sett.</span>
                <span className={styles.kvValue}>{m.monotonia.toFixed(1)}</span>
              </div>
            )}
            {m.strain != null && (
              <div className={styles.kvRow}>
                <span className={styles.kvLabel}>Strain sett.</span>
                <span className={styles.kvValue}>{m.strain.toFixed(0)}</span>
              </div>
            )}
          </div>
        )}

        {/* ─── Breakdown IPS componenti (v1.1.1 sez 7) ── */}
        {m.ipsBreakdownAvg && (
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>
              Breakdown IPS (componenti)
            </div>
            {[
              { label: 'S_app (spazio/giocatore)', value: m.ipsBreakdownAvg.s_app, max: 2.5 },
              { label: 'S_ewt (tempo lavoro)', value: m.ipsBreakdownAvg.s_ewt, max: 2.5 },
              { label: 'S_struct (struttura)', value: m.ipsBreakdownAvg.s_struct, max: 2.5 },
              { label: 'S_tactical (specificità)', value: m.ipsBreakdownAvg.s_tactical, max: 2.5 },
            ].map(({ label, value, max }) => (
              <div key={label} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>{label}</span>
                <div className={styles.breakdownBar}>
                  <div
                    className={styles.breakdownFill}
                    style={{ width: `${((value ?? 0) / max) * 100}%` }}
                  />
                </div>
                <span className={styles.breakdownValue}>
                  {value?.toFixed(2) ?? '—'}
                </span>
              </div>
            ))}
            <div className={styles.breakdownTotal}>
              Totale IPS: <strong>{m.avgIPS?.toFixed(1) ?? '—'}</strong>
              {m.peakIPS != null && (
                <span className={styles.breakdownPeak}>
                  picco {m.peakIPS.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ─── Breakdown CLS componenti (v1.1.1 sez 7) ── */}
        {m.clsBreakdownAvg && (
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>
              Breakdown CLS (componenti)
            </div>
            {[
              { label: 'Decisionale', value: m.clsBreakdownAvg.decisional, max: 4 },
              { label: 'Sociale', value: m.clsBreakdownAvg.social, max: 3 },
              { label: 'Tattica', value: m.clsBreakdownAvg.tactical, max: 3 },
            ].map(({ label, value, max }) => (
              <div key={label} className={styles.breakdownRow}>
                <span className={styles.breakdownLabel}>{label}</span>
                <div className={styles.breakdownBar}>
                  <div
                    className={styles.breakdownFill}
                    style={{
                      width: `${((value ?? 0) / max) * 100}%`,
                      background: '#7c5cbf',
                    }}
                  />
                </div>
                <span className={styles.breakdownValue}>
                  {value?.toFixed(2) ?? '—'}
                </span>
              </div>
            ))}
            <div className={styles.breakdownTotal}>
              Totale CLS: <strong>{m.avgCLS?.toFixed(1) ?? '—'}</strong>
              {m.peakCLS != null && (
                <span className={styles.breakdownPeak}>
                  picco {m.peakCLS.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ─── Suggerimenti rule-based ─────────────────── */}
        {suggestions.length > 0 && (
          <div className={styles.sectionCard}>
            <div className={styles.sectionTitle}>Suggerimenti</div>
            {suggestions.map((s, i) => (
              <div key={i} className={styles.suggestion}>
                <span
                  className={styles.suggestionDot}
                  style={{ background: '#ffd15a' }}
                />
                <span className={styles.suggestionText}>{s}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
